import { cloneElement, isValidElement, useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row, message } from "antd";
import {
  DashboardOutlined,
  DatabaseOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import { api, type Health } from "../api";
import { useSSE } from "../hooks/useSSE";
import LiveChart, { type ViewMode } from "../components/LiveChart";
import TopTalkersTable from "../components/TopTalkersTable";
import TotalsBar from "../components/TotalsBar";
import TopASNTable from "../components/TopASNTable";
import DashboardGrid, { type WidgetDef } from "../components/DashboardGrid";
import { useGeoSettings } from "../contexts/GeoSettingsContext";

// IXP-specific widget IDs — filtered out when IXP collection is disabled.
// (The NIX ASN widget is gated by nix_enabled below.)
const IXP_WIDGET_IDS = new Set([
  "top-ixp-bps-in",
  "top-ixp-bps-out",
  "top-asn-ixp-in",
  "top-asn-ixp-out",
]);
const NIX_WIDGET_IDS = new Set(["top-asn-nix-in", "top-asn-nix-out"]);

// ── widget definitions (full set) ─────────────────────────────────────────────

const ALL_WIDGETS: WidgetDef[] = [
  {
    id: "chart-global",
    // Bumped from h=10 to h=14 (~280px → ~392px tall) so the chart has a
    // ~3:2 aspect ratio matching Wansight's "Bits/s :: TINO :: IXP" panel.
    // Peaks read clearly instead of being a thin smear.
    defaultLayout: { x: 0, y: 0, w: 12, h: 14 },
    minW: 4,
    minH: 7,
    title: "Global Bandwidth",
    color: "var(--series-1)",
    icon: "≈",
    children: <LiveChart seconds={600} refreshMs={5000} height={320} />,
  },
  {
    id: "top-bps-in",
    defaultLayout: { x: 0, y: 14, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top Inbound Talkers",
    color: "var(--series-1)",
    icon: "↓",
    children: <TopTalkersTable metric="bps" dir="in" color="var(--series-1)" />,
  },
  {
    id: "top-bps-out",
    defaultLayout: { x: 6, y: 14, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top Outbound Talkers",
    color: "var(--series-7)",
    icon: "↑",
    children: <TopTalkersTable metric="bps" dir="out" color="var(--series-7)" />,
  },
  {
    id: "top-pps-in",
    defaultLayout: { x: 0, y: 25, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top Packets In",
    color: "var(--series-5)",
    icon: "↓",
    children: <TopTalkersTable metric="pps" dir="in" color="var(--series-5)" />,
  },
  {
    id: "top-pps-out",
    defaultLayout: { x: 6, y: 25, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top Packets Out",
    color: "var(--series-2)",
    icon: "↑",
    children: <TopTalkersTable metric="pps" dir="out" color="var(--series-2)" />,
  },
  {
    id: "top-ixp-bps-in",
    defaultLayout: { x: 0, y: 36, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top IXP Inbound",
    color: "var(--series-1)",
    icon: "↓",
    children: <TopTalkersTable metric="bps" dir="in" color="var(--series-1)" geo="ixp" />,
  },
  {
    id: "top-ixp-bps-out",
    defaultLayout: { x: 6, y: 36, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top IXP Outbound",
    color: "var(--series-3)",
    icon: "↑",
    children: <TopTalkersTable metric="bps" dir="out" color="var(--series-3)" geo="ixp" />,
  },
  // ── Top ASN widgets (powered by GeoLite2-ASN.mmdb) ──────────────────────
  {
    id: "top-asn-ixp-in",
    defaultLayout: { x: 0, y: 47, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top ASN · IXP Inbound",
    color: "var(--series-8)",
    icon: "↓",
    children: <TopASNTable geo="ixp" dir="in" metric="bps" color="var(--series-8)" />,
  },
  {
    id: "top-asn-ixp-out",
    defaultLayout: { x: 6, y: 47, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top ASN · IXP Outbound",
    color: "var(--series-7)",
    icon: "↑",
    children: <TopASNTable geo="ixp" dir="out" metric="bps" color="var(--series-7)" />,
  },
  {
    id: "top-asn-nix-in",
    defaultLayout: { x: 0, y: 58, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top ASN · NIX Inbound",
    color: "var(--series-1)",
    icon: "↓",
    children: <TopASNTable geo="nix" dir="in" metric="bps" color="var(--series-1)" />,
  },
  {
    id: "top-asn-nix-out",
    defaultLayout: { x: 6, y: 58, w: 6, h: 11 },
    minW: 3,
    minH: 4,
    title: "Top ASN · NIX Outbound",
    color: "var(--series-2)",
    icon: "↑",
    children: <TopASNTable geo="nix" dir="out" metric="bps" color="var(--series-2)" />,
  },
];

// ── Stat card component ─────────────────────────────────────────────────────

type StatTone = "ok" | "warn" | "err" | "neutral";

function StatCard({
  label,
  value,
  icon,
  tone,
  live,
  sm,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  tone: StatTone;
  live?: boolean;
  sm?: boolean;
}) {
  const toneClass =
    tone === "ok" ? "stat-ok" : tone === "warn" ? "stat-warn" : tone === "err" ? "stat-err" : "";
  const dotClass = live ? "live" : tone === "err" ? "err" : "";
  return (
    <div className={`stat-card ${toneClass}`}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-body">
        <div className="stat-label">
          <span className={`stat-dot ${dotClass}`} />
          {label}
        </div>
        <div className={`stat-value${sm ? " stat-value-sm" : ""}`}>{value}</div>
      </div>
    </div>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function Dashboard({ sensorId }: { sensorId?: number } = {}) {
  const [health, setHealth] = useState<Health | null>(null);
  const [loading, setLoading] = useState(true);
  const { tick, connected } = useSSE("/api/v1/events/stream");
  const { settings: geoSettings } = useGeoSettings();

  // Scope reported up from the global chart's View + Sensor pickers, so the
  // four TotalsBar cards above the chart track exactly what the chart shows
  // (was: cards stayed network-wide while the chart was narrowed). Seeded
  // with the locked sensorId in per-sensor mode; the chart confirms it on
  // mount and updates it on every picker change.
  const [scope, setScope] = useState<{ view: ViewMode; sensor: number | undefined }>({
    view: "total",
    sensor: sensorId,
  });
  const onScopeChange = useCallback(
    (view: ViewMode, sensor: number | undefined) => setScope({ view, sensor }),
    []
  );

  // Per-sensor mode: resolve the source's display name for the header.
  const [sensorName, setSensorName] = useState<string>("");
  useEffect(() => {
    if (sensorId == null) {
      setSensorName("");
      return;
    }
    api.sensors()
      .then((list) => setSensorName(list.find((s) => s.id === sensorId)?.name ?? `#${sensorId}`))
      .catch(() => setSensorName(`#${sensorId}`));
  }, [sensorId]);

  // Default time range + auto-refresh for the global chart, fetched once
  // from app_config. Readonly users can read this because the GET
  // /settings/live endpoint allows any authenticated role.
  const [chartSecs, setChartSecs] = useState<number>(3600);
  const [chartRefreshMs, setChartRefreshMs] = useState<number>(5000);
  useEffect(() => {
    api.liveSettings()
      .then((s) => {
        setChartSecs(s.chart_default_range_secs || 3600);
        // Allow 0 (off) — falsy check would coerce 0 to default 5000.
        if (typeof s.chart_default_refresh_ms === "number") {
          setChartRefreshMs(s.chart_default_refresh_ms);
        }
      })
      .catch(() => {}); // fall back silently
  }, []);

  // Gate IXP-only tiles when IXP collection is disabled, and NIX-ASN tiles
  // when NIX collection is disabled. (NIX IP tiles stay always on because
  // they include intra-zone traffic that isn't classified by Country.mmdb.)
  const widgets = useMemo(
    () =>
      ALL_WIDGETS.filter((w) => {
        if (IXP_WIDGET_IDS.has(w.id) && !geoSettings.ixp_enabled) return false;
        if (NIX_WIDGET_IDS.has(w.id) && !geoSettings.nix_enabled) return false;
        return true;
      }).map((w) => {
        // chart-global needs a fresh LiveChart so the configurable
        // `seconds` is also picked up — its own children prop is
        // recreated with both knobs from app_config.
        if (w.id === "chart-global") {
          return {
            ...w,
            children: (
              <LiveChart
                seconds={chartSecs}
                refreshMs={chartRefreshMs}
                height={320}
                sensorId={sensorId}
                onScopeChange={onScopeChange}
              />
            ),
          };
        }
        // Every other widget already has a children element (TopTalkers /
        // TopASN / TotalsBar). Clone it and inject the configured refresh
        // so the operator's "Default Refresh" choice in Settings governs
        // the WHOLE dashboard, not just the global chart. In per-sensor mode
        // we also inject sensorId so each widget scopes to that source;
        // widgets that don't accept it simply ignore the extra prop.
        if (isValidElement(w.children)) {
          return {
            ...w,
            children: cloneElement(
              w.children as React.ReactElement<{ refreshMs?: number; sensorId?: number }>,
              { refreshMs: chartRefreshMs, sensorId }
            ),
          };
        }
        return w;
      }),
    [geoSettings.ixp_enabled, geoSettings.nix_enabled, chartSecs, chartRefreshMs, sensorId, onScopeChange]
  );

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const h = await api.health();
      setHealth(h);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const lastTickAgo = tick
    ? `${Math.max(0, Math.floor(Date.now() / 1000 - tick.ts))}s ago`
    : "—";

  void loading; // suppress unused warning

  const apiOk = health?.status === "ok";
  const dbOk  = !!health?.db;

  return (
    <>
      {/* Page header */}
      <div className="page-header-band">
        <div className="ph-left">
          <div className="ph-icon">
            <DashboardOutlined />
          </div>
          <div>
            <div className="ph-title">
              {sensorId != null ? `${sensorName || `#${sensorId}`} :: Dashboard` : "Sensors Dashboard"}
            </div>
            <div className="ph-subtitle">
              {sensorId != null
                ? "Real-time traffic for this sFlow source only"
                : "Real-time network traffic monitoring"}
            </div>
          </div>
        </div>
      </div>

      {/* Status row */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            label="API"
            value={apiOk ? "Operational" : health?.status ?? "…"}
            icon={<ThunderboltOutlined />}
            tone={apiOk ? "ok" : "err"}
            live={apiOk}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            label="Uptime"
            value={health?.uptime ?? "…"}
            icon={<ClockCircleOutlined />}
            tone="neutral"
            sm
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            label="Database"
            value={dbOk ? "Connected" : "Down"}
            icon={<DatabaseOutlined />}
            tone={dbOk ? "ok" : "err"}
            live={dbOk}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            label="Live stream"
            value={connected ? lastTickAgo : "Disconnected"}
            icon={<WifiOutlined />}
            tone={connected ? "ok" : "err"}
            live={connected}
            sm
          />
        </Col>
      </Row>

      {/* Realtime totals — four big BPS/PPS cards above the chart.
          Polls /api/v1/live/totals at the operator-configured Default
          Refresh cadence (Settings → Live Top → Default Refresh) so the
          whole dashboard breathes at the same rate. */}
      <TotalsBar refreshMs={chartRefreshMs} sensorId={scope.sensor} view={scope.view} />

      {/* Draggable / resizable widget grid */}
      <DashboardGrid widgets={widgets} />
    </>
  );
}
